import { ApltFileType, IApltFile } from "./file-manager.types";

// -----------------------------------------------------------------------------------------------------
// @ ApltFile Model
// -----------------------------------------------------------------------------------------------------

/**
 * ApltFile model class that implements the IApltFile interface
 * Provides computed properties for UI display and file operations
 * Acts as the main data model for files and folders
 */
export class ApltFile implements Required<IApltFile> {
    // Core properties from API
    entityId: string;
    zCreationTimestamp: string | null;
    zTouchTimestamp: string | null;
    folderId: string | null;
    name: string;
    owner: string;
    size: number;
    type: ApltFileType;
    description: string | null;
    zcreationTimestamp: string | null;
    ztouchTimestamp: string | null;
    id: string | null;

    // Computed properties for Fuse template compatibility
    get createdBy(): string {
        return 'Current User'; // TODO: Integrate with Auth0
    }

    get createdAt(): string {
        return this.zcreationTimestamp || new Date().toISOString();
    }

    get modifiedAt(): string {
        return this.ztouchTimestamp || new Date().toISOString();
    }

    /**
     * Returns formatted size for display
     * Shows item count for folders, byte size for files
     */
    get sizeFormatted(): string {
        if (this.type === 'folder') {
            return `${this.getSubItemsCount()} items`;
        }
        return this.formatBytes(this.size);
    }

    get contents(): string | null {
        if (this.type === 'folder') {
            return `${this.getSubItemsCount()} items`;
        }
        return null;
    }

    /**
     * Constructor - initializes ApltFile from API response
     * Sets default values and normalizes timestamps
     */
    constructor(apltFile: IApltFile) {
        this.entityId = apltFile.entityId;
        this.zCreationTimestamp = apltFile.zCreationTimestamp || null;
        this.zTouchTimestamp = apltFile.zTouchTimestamp || null;
        this.folderId = apltFile.folderId || null;
        this.name = apltFile.name;
        this.owner = apltFile.owner;
        this.size = apltFile.size;
        this.type = apltFile.type;
        this.description = apltFile.description || null;
        this.zcreationTimestamp = apltFile.zcreationTimestamp || new Date().toISOString();
        this.ztouchTimestamp = apltFile.ztouchTimestamp || new Date().toISOString();
        this.id = apltFile.id || apltFile.entityId;
    }

    /**
     * Converts bytes to human-readable format (KB, MB, GB)
     */
    private formatBytes(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Placeholder for sub-items count calculation
     * Will be implemented by the service
     */
    private getSubItemsCount(): number {
        return 0; // Placeholder
    }
}